/**
 * Enhanced WebRTC Connection for Multi-Device Provider
 * Extends the existing WebRTC connection with multi-device specific features
 */

import type {
  ConnectionState,
  StreamQuality,
  CameraChannel,
  VoiceCommState,
  TwoWayAudioStatus
} from './types';

import type {
  WebRTCConfig as LiveWebRTCConfig
} from '../live/types';

import {
  startLiveStreamingWorkflow,
  stopLiveView,
  changeLiveDirectionWithRetry,
  startTwoWayAudio,
  stopTwoWayAudio
} from '../live/api';

// Channel mapping for FleetA API
const CHANNEL_MAPPING = {
  front: '1',
  rear: '2', 
  interior: '3',
  exterior: '4'
} as const;

// Quality settings for WebRTC
const QUALITY_SETTINGS = {
  high: {
    videoBitsPerSecond: 5000000,  // 5Mbps
    audioBitsPerSecond: 256000,   // 256kbps
    priority: 1
  },
  medium: {
    videoBitsPerSecond: 2500000,  // 2.5Mbps
    audioBitsPerSecond: 128000,   // 128kbps
    priority: 2
  },
  low: {
    videoBitsPerSecond: 1000000,  // 1Mbps
    audioBitsPerSecond: 64000,    // 64kbps
    priority: 3
  }
} as const;

/**
 * Connection configuration for multi-device WebRTC
 */
export interface MultiDeviceConnectionConfig {
  deviceId: string;
  channel: CameraChannel;
  quality: StreamQuality;
  clientId: string;
  role: 'VIEWER' | 'MASTER';
  enableStats: boolean;
  statsInterval: number;
  enableVoiceComm: boolean;
  
  // Event callbacks
  onStateChange?: (state: ConnectionState) => void;
  onError?: (error: Error) => void;
  onStreamReceived?: (stream: MediaStream) => void;
  onStatsUpdate?: (stats: ConnectionStats) => void;
  onVoiceCommStateChange?: (state: VoiceCommState) => void;
}

/**
 * Connection statistics for multi-device monitoring
 */
export interface ConnectionStats {
  bytesReceived: number;
  packetsReceived: number;
  packetsLost: number;
  jitter: number;
  roundTripTime: number;
  connectionId: string;
  deviceId: string;
  channel: CameraChannel;
  quality: StreamQuality;
  timestamp: number;
}

/**
 * Enhanced WebRTC Connection for Multi-Device scenarios
 * 기존 WebRTCConnection을 확장하여 다중 디바이스 지원
 */
export class MultiDeviceWebRTCConnection {
  // Configuration
  private config: MultiDeviceConnectionConfig;
  private webrtcConfig: LiveWebRTCConfig | null = null;
  
  // Connection state
  private state: ConnectionState = 'idle';
  private error: Error | null = null;
  private connectionId: string;
  
  // WebRTC objects
  private peerConnection: RTCPeerConnection | null = null;
  private signalingClient: any = null;
  private stream: MediaStream | null = null;
  
  // Statistics monitoring
  private stats: ConnectionStats | null = null;
  private statsInterval: NodeJS.Timeout | null = null;
  
  // Quality management
  private currentQuality: StreamQuality;
  private qualityConstraints: RTCConfiguration;
  
  // Voice communication
  private voiceCommState: VoiceCommState;
  private localAudioStream: MediaStream | null = null;
  private remoteAudioStream: MediaStream | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;
  
  /**
   * Create enhanced WebRTC connection for multi-device streaming
   */
  constructor(config: MultiDeviceConnectionConfig) {
    this.config = config;
    this.currentQuality = config.quality;
    this.connectionId = `${config.deviceId}_${config.channel}_${config.quality}_${Date.now()}`;
    
    // Set quality constraints based on configuration
    this.qualityConstraints = this.generateQualityConstraints(config.quality);
    
    // Initialize voice communication state
    this.voiceCommState = {
      isActive: false,
      isStarting: false,
      status: 'free',
      error: null,
      localAudioEnabled: true,
      remoteAudioVolume: 1.0,
      localAudioStream: null,
      remoteAudioStream: null
    };
    
  }

  /**
   * Start WebRTC connection with multi-device support
   */
  async connect(): Promise<void> {
    if (this.state === 'connecting' || this.state === 'connected') {
      return;
    }

    try {
      this.setState('connecting');
      this.setError(null);


      // Step 1: Get WebRTC configuration from FleetA API
      const channelParam = CHANNEL_MAPPING[this.config.channel];
      this.webrtcConfig = await startLiveStreamingWorkflow(
        this.config.deviceId,
        channelParam,
        this.config.clientId,
        this.config.role
      );

      // Step 2: Initialize WebRTC connection with quality settings
      await this.initializeWebRTCConnection(this.webrtcConfig);

      // Step 3: Start statistics monitoring if enabled
      if (this.config.enableStats) {
        this.startStatsMonitoring();
      }

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Connection failed');
      this.setError(errorObj);
      this.setState('failed');
      throw errorObj;
    }
  }

  /**
   * Disconnect WebRTC connection
   */
  async disconnect(): Promise<void> {
    try {
      // Stop voice communication if active
      if (this.voiceCommState.isActive) {
        await this.stopVoiceComm();
      }
      
      // Stop statistics monitoring
      this.stopStatsMonitoring();

      // Close peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Close signaling client
      if (this.signalingClient) {
        this.signalingClient.close();
        this.signalingClient = null;
      }

      // Clear stream reference
      this.stream = null;

      // Stop streaming on FleetA side
      try {
        await stopLiveView({ psn: this.config.deviceId });
      } catch (error) {
        // Ignore FleetA API errors during disconnect
      }

      this.setState('disconnected');
      
    } catch (error) {
      this.setState('disconnected'); // Force disconnected state even on error
    }
  }

  /**
   * Change channel while maintaining connection
   */
  async changeChannel(newChannel: CameraChannel): Promise<void> {
    if (newChannel === this.config.channel) {
      return;
    }

    if (this.state !== 'connected') {
      throw new Error(`Cannot change channel: connection not established (${this.state})`);
    }

    try {
      // Use FleetA API to change channel
      const channelParam = CHANNEL_MAPPING[newChannel];
      await changeLiveDirectionWithRetry({
        psn: this.config.deviceId,
        param1: channelParam
      });
      
      // Update configuration
      this.config.channel = newChannel;
      
      // Update connection ID to reflect new channel
      this.connectionId = `${this.config.deviceId}_${newChannel}_${this.currentQuality}_${Date.now()}`;
      
    } catch (error) {
      const errorMessage = `Channel change failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      const errorObj = new Error(errorMessage);
      this.setError(errorObj);
      throw errorObj;
    }
  }

  /**
   * Change stream quality
   */
  async changeQuality(newQuality: StreamQuality): Promise<void> {
    if (newQuality === this.currentQuality) {
      return;
    }

    try {
      // Update quality constraints
      this.qualityConstraints = this.generateQualityConstraints(newQuality);
      this.currentQuality = newQuality;
      
      // If connected, renegotiate with new quality
      if (this.peerConnection && this.state === 'connected') {
        await this.renegotiateWithNewQuality();
      }
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start voice communication
   * 양방향 음성 통신 시작
   */
  async startVoiceComm(): Promise<TwoWayAudioStatus> {
    if (!this.config.enableVoiceComm) {
      throw new Error('Voice communication not enabled for this connection');
    }
    
    if (this.voiceCommState.isActive) {
      return this.voiceCommState.status;
    }
    
    if (this.state !== 'connected') {
      throw new Error('Connection must be established before starting voice communication');
    }
    
    try {
      this.updateVoiceCommState({ isStarting: true, error: null });
      
      // Step 1: Start FleetA API two-way audio
      const response = await startTwoWayAudio({
        psn: this.config.deviceId,
        tokenType: 'web'
      });
      
      if (response.status === 'busy') {
        this.updateVoiceCommState({ 
          isStarting: false, 
          status: 'busy',
          error: 'Voice communication is busy. Another user is currently using it.'
        });
        throw new Error('Voice communication is busy. Another user is currently using it.');
      }
      
      // Step 2: Start WebRTC audio streams
      await this.startWebRTCAudio();
      
      this.updateVoiceCommState({
        isActive: true,
        isStarting: false,
        status: 'active',
        error: null
      });
      
      return response.status || 'active';
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start voice communication';
      
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'error',
        error: errorMessage
      });
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Stop voice communication
   * 양방향 음성 통신 중지
   */
  async stopVoiceComm(): Promise<TwoWayAudioStatus> {
    if (!this.voiceCommState.isActive) {
      return 'free';
    }
    
    try {
      // Step 1: Stop WebRTC audio streams
      await this.stopWebRTCAudio();
      
      // Step 2: Stop FleetA API two-way audio
      const response = await stopTwoWayAudio({
        psn: this.config.deviceId,
        tokenType: 'web'
      });
      
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'free',
        error: null,
        localAudioStream: null,
        remoteAudioStream: null
      });
      
      return response.status || 'free';
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop voice communication';
      
      // Force reset state even on error
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'error',
        error: errorMessage
      });
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Toggle local audio (microphone mute/unmute)
   */
  setLocalAudioEnabled(enabled: boolean): void {
    if (!this.localAudioStream) {
      return;
    }
    
    this.localAudioStream.getAudioTracks().forEach(track => {
      track.enabled = enabled;
    });
    
    this.updateVoiceCommState({ localAudioEnabled: enabled });
    console.log(`🎤 [${this.connectionId}] Local audio ${enabled ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Set remote audio volume
   */
  setRemoteAudioVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    
    if (this.remoteAudioElement) {
      this.remoteAudioElement.volume = clampedVolume;
    }
    
    this.updateVoiceCommState({ remoteAudioVolume: clampedVolume });
  }
  
  /**
   * Get voice communication state
   */
  getVoiceCommState(): VoiceCommState {
    return { ...this.voiceCommState };
  }
  
  /**
   * Get current MediaStream
   */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Get current connection statistics
   */
  getStats(): ConnectionStats | null {
    return this.stats;
  }

  /**
   * Get connection information
   */
  getConnectionInfo(): {
    id: string;
    deviceId: string;
    channel: CameraChannel;
    quality: StreamQuality;
    state: ConnectionState;
  } {
    return {
      id: this.connectionId,
      deviceId: this.config.deviceId,
      channel: this.config.channel,
      quality: this.currentQuality,
      state: this.state
    };
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    // Stop voice communication if active
    if (this.voiceCommState.isActive) {
      this.stopVoiceComm().catch(error => {
      });
    }
    
    this.stopStatsMonitoring();
    
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    
    if (this.signalingClient) {
      this.signalingClient.close();
      this.signalingClient = null;
    }
    
    this.stream = null;
    this.webrtcConfig = null;
    this.setState('idle');
  }

  // ========================
  // PRIVATE METHODS
  // ========================

  /**
   * Initialize WebRTC connection with KVS WebRTC SDK
   */
  private async initializeWebRTCConnection(signalingInfo: LiveWebRTCConfig): Promise<void> {
    try {
      // Verify WebRTC SDK is available
      if (!window.KVSWebRTC) {
        throw new Error('KVS WebRTC SDK not loaded');
      }

      // Create custom signer for presigned URL
      const customSigner = {
        getSignedURL: () => signalingInfo.url
      };

      // Create PeerConnection with quality constraints
      this.peerConnection = new RTCPeerConnection({
        iceServers: signalingInfo.iceServers.map(server => ({
          urls: server.urls,
          username: server.username,
          credential: server.credential
        })),
        ...this.qualityConstraints
      });

      // Set up stream handling
      this.peerConnection.ontrack = (event) => {
        if (event.streams[0]) {
          // Check if this is an audio track for voice communication
          if (event.track?.kind === 'audio' && this.voiceCommState.isActive) {
            this.handleRemoteAudioStream(event.streams[0]);
          }
          
          this.stream = event.streams[0];
          this.config.onStreamReceived?.(event.streams[0]);
          this.setState('connected');
        }
      };

      // Set up ICE connection state monitoring
      this.peerConnection.oniceconnectionstatechange = () => {
        switch (this.peerConnection?.iceConnectionState) {
          case 'connected':
          case 'completed':
            this.setState('connected');
            break;
          case 'disconnected':
            this.setState('disconnected');
            break;
          case 'failed':
            this.setError(new Error('ICE connection failed'));
            this.setState('failed');
            break;
        }
      };

      // Create and setup SignalingClient
      this.signalingClient = new window.KVSWebRTC.SignalingClient({
        requestSigner: customSigner,
        region: signalingInfo.region,
        role: window.KVSWebRTC.Role.VIEWER,
        clientId: this.config.clientId,
        channelARN: signalingInfo.channelARN,
        channelEndpoint: signalingInfo.endpointsByProtocol.WSS
      });

      // Set up signaling event handlers
      this.setupSignalingHandlers();

      // Open signaling connection
      this.signalingClient.open();
      
    } catch (error) {
      throw new Error(`WebRTC initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Setup signaling client event handlers
   */
  private setupSignalingHandlers(): void {
    if (!this.signalingClient || !this.peerConnection) return;

    // Handle signaling connection open
    this.signalingClient.on('open', async () => {
      try {
        const offer = await this.peerConnection!.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        });
        
        await this.peerConnection!.setLocalDescription(offer);
        this.signalingClient.sendSdpOffer(this.peerConnection!.localDescription);
        
      } catch (error) {
        this.setError(new Error(`SDP offer failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
        this.setState('failed');
      }
    });

    // Handle SDP Answer
    this.signalingClient.on('sdpAnswer', async (answer: any) => {
      try {
        await this.peerConnection!.setRemoteDescription(answer);
      } catch (error) {
        this.setError(new Error(`SDP answer failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    });

    // Handle ICE candidates
    this.signalingClient.on('iceCandidate', async (candidate: any) => {
      try {
        await this.peerConnection!.addIceCandidate(candidate);
      } catch (error) {
        // Ignore ICE candidate errors
      }
    });

    // Handle local ICE candidates
    this.peerConnection!.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.signalingClient.sendIceCandidate(candidate);
      }
    };

    // Handle signaling errors
    this.signalingClient.on('error', (error: any) => {
      this.setError(new Error(`Signaling error: ${error.message || error}`));
      this.setState('failed');
    });
  }

  /**
   * Generate quality constraints based on quality level
   */
  private generateQualityConstraints(quality: StreamQuality): RTCConfiguration {
    const settings = QUALITY_SETTINGS[quality];
    
    return {
      // Add quality-specific constraints
      bundlePolicy: 'balanced',
      rtcpMuxPolicy: 'require',
      iceCandidatePoolSize: quality === 'high' ? 4 : quality === 'medium' ? 2 : 1
    };
  }

  /**
   * Renegotiate connection with new quality settings
   */
  private async renegotiateWithNewQuality(): Promise<void> {
    if (!this.peerConnection) return;

    try {
      // Create new offer with updated constraints
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });

      await this.peerConnection.setLocalDescription(offer);
      
      if (this.signalingClient) {
        this.signalingClient.sendSdpOffer(this.peerConnection.localDescription);
      }
      
    } catch (error) {
      console.error(`❌ [${this.connectionId}] Quality renegotiation failed:`, error);
    }
  }

  /**
   * Start WebRTC audio streams for voice communication
   */
  private async startWebRTCAudio(): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not available');
    }
    
    try {
      console.log(`🎤 [${this.connectionId}] Starting WebRTC audio...`);
      
      // Step 1: Get local audio stream (microphone)
      this.localAudioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false
      });
      
      console.log(`🎤 [${this.connectionId}] Local audio stream captured`);
      
      // Step 2: Add local audio tracks to peer connection
      this.localAudioStream.getAudioTracks().forEach(track => {
        console.log(`🎤 [${this.connectionId}] Adding local audio track to peer connection`);
        this.peerConnection!.addTrack(track, this.localAudioStream!);
      });
      
      // Step 3: Update voice comm state
      this.updateVoiceCommState({
        localAudioStream: this.localAudioStream
      });
      
      // Step 4: Trigger renegotiation for audio
      await this.renegotiateForAudio();
      
      console.log(`✅ [${this.connectionId}] WebRTC audio started successfully`);
      
    } catch (error) {
      console.error(`❌ [${this.connectionId}] Failed to start WebRTC audio:`, error);
      throw new Error(`Failed to start WebRTC audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Stop WebRTC audio streams
   */
  private async stopWebRTCAudio(): Promise<void> {
    try {
      console.log(`🎤 [${this.connectionId}] Stopping WebRTC audio...`);
      
      // Stop local audio stream
      if (this.localAudioStream) {
        this.localAudioStream.getTracks().forEach(track => {
          track.stop();
          console.log(`🎤 [${this.connectionId}] Local audio track stopped`);
        });
        this.localAudioStream = null;
      }
      
      // Stop remote audio playback
      if (this.remoteAudioElement) {
        this.remoteAudioElement.pause();
        this.remoteAudioElement.srcObject = null;
        this.remoteAudioElement = null;
      }
      
      // Remove audio tracks from peer connection
      if (this.peerConnection) {
        const senders = this.peerConnection.getSenders();
        const audioSenders = senders.filter(sender => 
          sender.track && sender.track.kind === 'audio'
        );
        
        for (const sender of audioSenders) {
          await this.peerConnection.removeTrack(sender);
          console.log(`🎤 [${this.connectionId}] Removed audio track from peer connection`);
        }
      }
      
      this.remoteAudioStream = null;
      
      console.log(`✅ [${this.connectionId}] WebRTC audio stopped successfully`);
      
    } catch (error) {
      console.error(`❌ [${this.connectionId}] Failed to stop WebRTC audio:`, error);
      // Don't throw error on stop, just log it
    }
  }
  
  /**
   * Handle remote audio stream received
   */
  private handleRemoteAudioStream(stream: MediaStream): void {
    this.remoteAudioStream = stream;
    
    // Create audio element for remote audio playback
    this.remoteAudioElement = new Audio();
    this.remoteAudioElement.srcObject = stream;
    this.remoteAudioElement.autoplay = true;
    this.remoteAudioElement.volume = this.voiceCommState.remoteAudioVolume;
    
    // Handle audio element events
    this.remoteAudioElement.onloadedmetadata = () => {
    };
    
    this.remoteAudioElement.onplay = () => {
    };
    
    this.remoteAudioElement.onerror = (error) => {
    };
    
    // Update voice comm state
    this.updateVoiceCommState({
      remoteAudioStream: stream
    });
  }
  
  /**
   * Renegotiate peer connection for audio
   */
  private async renegotiateForAudio(): Promise<void> {
    if (!this.peerConnection || !this.signalingClient) {
      throw new Error('Peer connection or signaling client not available');
    }
    
    try {
      console.log(`🔄 [${this.connectionId}] Renegotiating for audio...`);
      
      // Create new offer with audio
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      // Set local description
      await this.peerConnection.setLocalDescription(offer);
      
      // Send offer through signaling
      this.signalingClient.sendSdpOffer(this.peerConnection.localDescription);
      
      console.log(`✅ [${this.connectionId}] Audio renegotiation offer sent`);
      
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Update voice communication state and notify callbacks
   */
  private updateVoiceCommState(updates: Partial<VoiceCommState>): void {
    this.voiceCommState = { ...this.voiceCommState, ...updates };
    this.config.onVoiceCommStateChange?.(this.voiceCommState);
  }
  
  /**
   * Start connection statistics monitoring
   */
  private startStatsMonitoring(): void {
    if (this.statsInterval) return;

    this.statsInterval = setInterval(async () => {
      if (this.peerConnection && this.state === 'connected') {
        try {
          const stats = await this.peerConnection.getStats();
          let bytesReceived = 0;
          let packetsReceived = 0;
          let packetsLost = 0;
          let jitter = 0;
          let roundTripTime = 0;

          stats.forEach((report) => {
            if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
              bytesReceived += report.bytesReceived || 0;
              packetsReceived += report.packetsReceived || 0;
              packetsLost += report.packetsLost || 0;
              jitter = report.jitter || 0;
            }
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              roundTripTime = report.currentRoundTripTime || 0;
            }
          });

          this.stats = {
            bytesReceived,
            packetsReceived,
            packetsLost,
            jitter,
            roundTripTime,
            connectionId: this.connectionId,
            deviceId: this.config.deviceId,
            channel: this.config.channel,
            quality: this.currentQuality,
            timestamp: Date.now()
          };

          this.config.onStatsUpdate?.(this.stats);
          
        } catch (error) {
          // Ignore stats collection errors
        }
      }
    }, this.config.statsInterval);
  }

  /**
   * Stop connection statistics monitoring
   */
  private stopStatsMonitoring(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }

  /**
   * Set connection state and notify callbacks
   */
  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.config.onStateChange?.(state);
    }
  }

  /**
   * Set error and notify callbacks
   */
  private setError(error: Error | null): void {
    this.error = error;
    if (error) {
      this.config.onError?.(error);
    }
  }
}

// Extend window interface for KVS WebRTC
declare global {
  interface Window {
    KVSWebRTC: {
      SignalingClient: any;
      Role: {
        VIEWER: string;
        MASTER: string;
      };
    };
  }
}